import { useAuth } from "@/context/authContext";
import { api } from "@/utils/api";
import { useState } from "react";
import { toast } from "sonner";

export type ManageApiBase = "admin" | "advisor";

const enrollmentBase = (apiBase: ManageApiBase) =>
  apiBase === "advisor" ? "/enrollments/advisor" : "/enrollments/admin";

export const useManageStudents = (
  { apiBase }: { apiBase?: ManageApiBase } = {},
) => {
  const base: ManageApiBase = apiBase ?? "admin";
  const { dispatch } = useAuth();
  const [loading, setLoading] = useState<{
    enrollment: boolean;
    removeEnrollment: boolean;
    preferences: boolean;
    removePreferences: boolean;
    attendance: boolean;
    advisor: boolean;
  }>({
    enrollment: false,
    removeEnrollment: false,
    preferences: false,
    removePreferences: false,
    attendance: false,
    advisor: false,
  });

  const saveEnrollment = async (
    studentId: string,
    sessionId: string,
    explorationId?: string,
  ) => {
    try {
      if (!explorationId) return toast.error("No exploration selected");
      setLoading((prev) => ({ ...prev, enrollment: true }));
      const response = await api(
        `${enrollmentBase(base)}/${studentId}/${sessionId}`,
        { method: "PUT", body: { explorationId } },
        dispatch,
      );
      toast.success(response.message);
      return response;
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, enrollment: false }));
    }
  };

  const removeEnrollment = async (studentId: string, sessionId: string) => {
    try {
      setLoading((prev) => ({ ...prev, removeEnrollment: true }));
      const response = await api(
        `${enrollmentBase(base)}/${studentId}/${sessionId}`,
        { method: "DELETE" },
        dispatch,
      );
      toast.success(response.message);
      return response;
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, removeEnrollment: false }));
    }
  };

  const savePreferences = async (
    studentId: string,
    sessionId: string,
    choices?: [string, string, string],
  ) => {
    try {
      if (!choices) return toast.error("No preferences selected");
      setLoading((prev) => ({ ...prev, preferences: true }));
      const response = await api(
        `${enrollmentBase(base)}/preferences/${studentId}/${sessionId}`,
        { method: "PUT", body: { choices } },
        dispatch,
      );
      toast.success(response.message);
      return response;
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, preferences: false }));
    }
  };

  const removePreferences = async (studentId: string, sessionId: string) => {
    try {
      setLoading((prev) => ({ ...prev, removePreferences: true }));
      const response = await api(
        `${enrollmentBase(base)}/preferences/${studentId}/${sessionId}`,
        { method: "DELETE" },
        dispatch,
      );
      toast.success(response.message);
      return response;
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, removePreferences: false }));
    }
  };

  const saveAttendance = async (
    studentId: string,
    sessionId: string,
    entries?: { classId: string; present: boolean; reason?: string }[],
  ) => {
    try {
      if (!entries) return toast.error("No entries found");
      setLoading((prev) => ({ ...prev, attendance: true }));
      const response = await api(
        `/attendance/admin/${studentId}/${sessionId}`,
        { method: "PUT", body: { entries } },
        dispatch,
      );
      toast.success(response.message);
      return response;
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, attendance: false }));
    }
  };

  const saveAdvisor = async (
    studentId: string,
    advisorId: string | null,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, advisor: true }));
      const response = await api(
        `/auth/users/${studentId}/advisor`,
        { method: "PUT", body: { advisorId } },
        dispatch,
      );
      toast.success(response.message);
      return response;
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, advisor: false }));
    }
  };

  return {
    saveEnrollment,
    removeEnrollment,
    savePreferences,
    removePreferences,
    saveAttendance,
    saveAdvisor,
    loading,
  };
};
